import * as React from 'react';
import { observer } from 'mobx-react-lite';
import type CalendarEvent from 'owa-calendar-types/lib/types/CalendarEvent';
import type { ColorScheme } from 'owa-color-utils/lib/ColorScheme';
import {
    singleTimeProposalSummary,
    singleTimeProposalSummaryNoTime,
    multipleTimeProposalSummary,
} from './TimeProposals.locstring.json';
import loc, { format, formatToArray } from 'owa-localize';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import { userDate } from 'owa-datetime';
import { getCalendarEventColorScheme } from 'owa-calendar-color-resolver';
import { Link } from '@fluentui/react/lib/Link';
import generateLinkStyle from 'owa-component-styling-utils/lib/utils/colorSchemesToStyles/generateLinkStyle';
import PersonaName from './PersonaName';

import styles from './TimeProposals.scss';
import classNames from 'classnames';

export interface TimeProposalsProps {
    event: CalendarEvent;
    onReviewClicked: (event?: Partial<CalendarEvent>) => void;
    hideTime?: boolean;
    className?: string;
    colorScheme?: ColorScheme;
}

const TimeProposals: React.FunctionComponent<TimeProposalsProps> = props => {
    const { event, onReviewClicked, hideTime, className, colorScheme } = props;

    const newTimeProposers =
        event.RequiredAttendees && event.OptionalAttendees
            ? event.RequiredAttendees.concat(event.OptionalAttendees).filter(
                  attendee => attendee.ProposedStart
              )
            : [];

    const onClick = React.useCallback(() => {
        onReviewClicked(
            newTimeProposers.length === 1
                ? generatePartialEventForProposeNewTime(
                      newTimeProposers[0].ProposedStart,
                      newTimeProposers[0].ProposedEnd
                  )
                : null
        );
    }, [newTimeProposers]);

    if (newTimeProposers.length === 0) {
        return null;
    } else {
        return (
            <Link
                styles={generateLinkStyle(colorScheme || getCalendarEventColorScheme(event))}
                className={classNames(styles.container, className)}
                onClick={onClick}>
                {newTimeProposers.length === 1
                    ? hideTime
                        ? formatToArray(
                              loc(singleTimeProposalSummaryNoTime),
                              <PersonaName
                                  name={newTimeProposers[0].Mailbox.Name}
                                  emailAddress={newTimeProposers[0].Mailbox.EmailAddress}
                              />
                          )
                        : formatToArray(
                              loc(singleTimeProposalSummary),
                              <PersonaName
                                  name={newTimeProposers[0].Mailbox.Name}
                                  emailAddress={newTimeProposers[0].Mailbox.EmailAddress}
                              />,
                              <span>
                                  {formatMeetingTimeSpan(
                                      newTimeProposers[0].ProposedStart,
                                      newTimeProposers[0].ProposedEnd
                                  )}
                              </span>
                          )
                    : format(loc(multipleTimeProposalSummary), newTimeProposers.length)}
            </Link>
        );
    }
};

export default observer(TimeProposals);

function generatePartialEventForProposeNewTime(
    proposedStart: string,
    proposedEnd: string
): Partial<CalendarEvent> {
    return {
        Start: userDate(proposedStart),
        End: userDate(proposedEnd),
        IsAllDayEvent: false, // propose new time does not support all day
    };
}
